import { Component } from '@theme/component';

const FORCE_PARAM = 'cookie_banner';
const DISMISSED_KEY = 'cookie_banner_dismissed';

/**
 * Cookie banner without consent logic (same as the DE shop): Accept and Decline both only close it,
 * tracking runs regardless. Shows until the visitor clicks either button (remembered in localStorage),
 * when forced with `?cookie_banner=show`, or while selected in the theme editor.
 * The section renders it only where it is meant to appear (see `sections/cookie-banner.liquid`).
 * @extends {Component}
 */
class CookieBannerComponent extends Component {
  #forced = new URLSearchParams(window.location.search).get(FORCE_PARAM) === 'show';

  connectedCallback() {
    super.connectedCallback();

    if (this.#forced || (!window.Shopify?.designMode && !this.#dismissed())) this.#show();

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.#onEditorDeselect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('shopify:section:select', this.#onEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.#onEditorDeselect);
  }

  accept() {
    this.#dismiss();
  }

  decline() {
    this.#dismiss();
  }

  #dismissed() {
    try {
      return window.localStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  }

  #dismiss() {
    if (!this.#forced) {
      try {
        window.localStorage.setItem(DISMISSED_KEY, '1');
      } catch {
        // Storage blocked: the banner simply shows again on the next page.
      }
    }
    this.#hide();
  }

  #show() {
    this.hidden = false;
    requestAnimationFrame(() => this.setAttribute('data-open', ''));
  }

  #hide() {
    this.removeAttribute('data-open');
    const done = () => {
      this.hidden = true;
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      done();
    } else {
      this.addEventListener('transitionend', done, { once: true });
    }
  }

  /** @param {Event} event */
  #onEditorSelect = (event) => {
    if (event.target instanceof Element && event.target.contains(this)) this.#show();
  };

  /** @param {Event} event */
  #onEditorDeselect = (event) => {
    if (event.target instanceof Element && event.target.contains(this)) this.#hide();
  };
}

if (!customElements.get('cookie-banner-component')) {
  customElements.define('cookie-banner-component', CookieBannerComponent);
}
